import   React        ,
       { useCallback   ,
         useEffect     ,
         useRef        ,
         useState      }  from "react";

import   classNames       from 'classnames';

import   styles           from './CommentModerationList.module.scss';

import { useTypedSelector } from '../../../store/useTypeSelector';
import { RootState        } from '../../../store/generalReducer';

import { ModerationComment ,
         ModerationAction  }  from '../../../dataTypes/moderationManagment/types';

import { CHAT_CATEGORY_LABELS   ,
         CHAT_MODERATION_LEVELS } from '../../../dataTypes/moderationManagment/chat_types';

import { getPendingComments ,
         moderateComment     }  from '../../../backendServices/moderationManagment/api_comment';

import { playChime ,
         pct       ,
         riskColor }  from '../chatModerationManagment/chatModerationUi';

/**
 * Commentaires vidéo à valider — onglet de l'écran Modération.
 *
 * Depuis le 2026-10-08, les commentaires sont triés par l'IA de Google comme le
 * chat en direct (customer-api, GoogleChatModerator, niveau Standard) : ceux
 * qu'elle ne publie ni ne refuse d'elle-même arrivent ici, avec son analyse —
 * le risque (même échelle et mêmes couleurs que le chat) et les catégories
 * relevées. Chaque créateur ne voit que les commentaires de ses vidéos ; un
 * super-admin les voit tous, avec le nom du créateur.
 *
 * La file se relit toute seule (5 s, 20 s quand l'onglet du navigateur est
 * caché) ; un nouveau commentaire s'annonce par le son du chat et une ligne
 * marquée « Nouveau » une minute.
 *
 * Démonstration sans serveur : `?demoCommentaires` dans l'adresse.
 */

const REFRESH_MS        = 5_000;
const REFRESH_HIDDEN_MS = 20_000;
const FRESH_MS          = 60_000;

/** Les seuils du niveau Standard, celui qui trie les commentaires. */
const STANDARD = CHAT_MODERATION_LEVELS.find(l => l.value === 1)!;

interface Props {
  /** Le nombre de commentaires à valider, pour les titres d'onglets. */
  onPendingCount? : (count: number) => void;
}

const CommentModerationList: React.FC<Props> = ({ onPendingCount }) => {

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const adminId = authentication.admin?.id ?? null;
  const demo    = isDemo();

  const [comments   , setComments   ] = useState<ModerationComment[]>([]);
  const [isLoading  , setIsLoading  ] = useState<boolean>(false);
  const [loaded     , setLoaded     ] = useState<boolean>(false);
  const [error      , setError      ] = useState<string>("");
  const [busyId     , setBusyId     ] = useState<number | null>(null);
  const [fresh      , setFresh      ] = useState<Map<number, number>>(new Map());
  const [updated    , setUpdated    ] = useState<Date | null>(null);

  const seen   = useRef<Set<number>>(new Set());
  const primed = useRef(false);

  /** `manual` : seul le bouton Rafraîchir affiche « Chargement… ». */
  const loadComments = useCallback(async (manual = false) => {

    if (!demo && adminId === null) return;
    if (manual) setIsLoading(true);

    const response = demo
      ? { code: 1, comment: "", payload: demoComments() }
      : await getPendingComments(adminId as number);

    if (response.code === 1)
     {
      // Les commentaires arrivés depuis le dernier relevé (pas au premier).
      const news = response.payload.filter(c => !seen.current.has(c.id)).map(c => c.id);
      response.payload.forEach(c => seen.current.add(c.id));
      if (primed.current && news.length > 0)
       {
        const until = Date.now() + FRESH_MS;
        setFresh(previous => { const next = new Map(previous); news.forEach(id => next.set(id, until)); return next; });
        playChime();
       }
      primed.current = true;
      setComments(response.payload);
      setUpdated(new Date());
      setError("");
     }
    else
     {
      setError(response.comment || "Erreur lors du chargement des commentaires.");
     }

    setLoaded(true);
    if (manual) setIsLoading(false);
  }, [adminId, demo]);

  // Relevé en continu, un délai qui se reprogramme : jamais deux requêtes à la fois.
  useEffect(() => {
    let stopped = false;
    let timer: number | undefined;
    const tick = async () => {
      await loadComments();
      if (!stopped) timer = window.setTimeout(tick, document.hidden ? REFRESH_HIDDEN_MS : REFRESH_MS);
    };
    tick();
    const onVisible = () => { if (!document.hidden) { window.clearTimeout(timer); tick(); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadComments]);

  // Les marques « Nouveau » s'effacent d'elles-mêmes.
  useEffect(() => {
    if (fresh.size === 0) return;
    const next  = Math.min(...Array.from(fresh.values()));
    const timer = window.setTimeout(() => {
      const now = Date.now();
      setFresh(previous => new Map(Array.from(previous).filter(([, until]) => until > now)));
    }, Math.max(0, next - Date.now()) + 50);
    return () => window.clearTimeout(timer);
  }, [fresh]);

  useEffect(() => { onPendingCount?.(comments.length); }, [comments.length, onPendingCount]);

  const handleModerate = async (commentId: number, action: ModerationAction) => {

    setBusyId(commentId);

    const response = demo
      ? { code: 1, comment: "" }
      : await moderateComment(adminId as number, commentId, action);

    if (response.code === 1)
     {
      // On retire le commentaire traité de la liste.
      setComments(previous => previous.filter(c => c.id !== commentId));
      if (demo) DEMO_DONE.add(commentId);
     }
    else
     {
      setError(response.comment || "Erreur lors de la modération.");
     }

    setBusyId(null);
  };

  const showCreator = comments.some(c => c.creator);

  return (
    <div className={styles.container}>

      {demo && (
        <div className="mb-3 p-2 rounded bg-yellow-100 text-yellow-800 text-sm">
          Démonstration : commentaires fictifs, rien n'est envoyé au serveur. Un nouveau arrive au bout de 15 secondes.
        </div>
      )}

      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="text-xl font-semibold">
          Modération des commentaires
          <span className="ml-2 text-sm font-normal text-gray-500">
            ({comments.length} en attente)
          </span>
        </h2>
        <div className="flex items-center gap-3">
          <span className={styles.live} title="La liste se met à jour toute seule toutes les 5 secondes">
            <span className={classNames(styles.liveDot, error !== "" && styles.liveDotOff)} />
            {error !== "" ? 'Hors ligne' : updated ? `En direct · ${updated.toLocaleTimeString('fr-FR')}` : 'En direct'}
          </span>
          <button
            type="button"
            onClick={() => loadComments(true)}
            disabled={isLoading}
            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          >
            Rafraîchir
          </button>
        </div>
      </div>

      <p className={styles.hint}>
        Les commentaires sont triés par l'IA comme le chat (niveau Standard) : elle publie ceux qui ne posent pas
        de problème et refuse les plus graves. Ceux-ci, douteux ou contenant un lien, attendent votre décision.
      </p>

      {error !== "" && (
        <div className="mb-4 p-2 rounded bg-red-100 text-red-700">
          {error}
        </div>
      )}

      {isLoading || !loaded ? (
        <div className="text-gray-500">Chargement…</div>
      ) : comments.length === 0 ? (
        <div className="text-gray-500">Aucun commentaire en attente de modération.</div>
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-left border-b">
              <th className="py-2 pr-4">Auteur</th>
              <th className="py-2 pr-4">Vidéo</th>
              <th className="py-2 pr-4">Commentaire</th>
              <th className="py-2 pr-4">IA</th>
              <th className="py-2 pr-4">Date</th>
              <th className="py-2 pr-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {comments.map(comment => (
              <tr key={comment.id} className={classNames("border-b", fresh.has(comment.id) && styles.rowFresh)}>
                <td className={`py-2 pr-4 ${styles.cell}`}>
                  {fresh.has(comment.id) && <span className={styles.tagFresh}>Nouveau</span>}
                  {comment.author}
                </td>
                <td className={`py-2 pr-4 ${styles.cell}`}>
                  {comment.videoTitle ?? `#${comment.videoId ?? '?'}`}
                  {showCreator && comment.creator && <div className="text-xs text-gray-500">{comment.creator}</div>}
                </td>
                <td className={`py-2 pr-4 ${styles.cell} whitespace-pre-wrap`}>{comment.text}</td>
                <td className={`py-2 pr-4 ${styles.cell}`}>
                  <AiVerdict comment={comment} />
                </td>
                <td className={`py-2 pr-4 ${styles.cell} whitespace-nowrap`}>
                  {comment.creationDate ? comment.creationDate.toLocaleString('fr-FR') : '—'}
                </td>
                <td className={`py-2 pr-4 ${styles.cell} whitespace-nowrap`}>
                  <button
                    type="button"
                    onClick={() => handleModerate(comment.id, 'approve')}
                    disabled={busyId === comment.id}
                    className="mr-2 px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    Approuver
                  </button>
                  <button
                    type="button"
                    onClick={() => handleModerate(comment.id, 'reject')}
                    disabled={busyId === comment.id}
                    className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    Rejeter
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

    </div>
  );
};

/** Le verdict de l'IA : le risque, sur l'échelle et aux couleurs du chat, puis
 *  les catégories relevées par Google. */
const AiVerdict: React.FC<{ comment: ModerationComment }> = ({ comment }) => {
  if (comment.aiScore === null) {
    return <span className="text-xs text-gray-500">{comment.aiEngine === 'aucun' || comment.aiEngine === null ? 'Sans analyse' : 'IA indisponible'}</span>;
  }
  const categories = Object.entries(comment.aiCategories)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);
  return (
    <div className={styles.ai}>
      <span className={styles.risk} style={{ background: riskColor(comment.aiScore, STANDARD.publish, STANDARD.reject) }}
            title={`Risque estimé par l'IA (${comment.aiEngine === 'claude' ? 'Claude, en secours' : 'Google'}) : publié sous ${pct(STANDARD.publish)}, refusé à partir de ${pct(STANDARD.reject)}`}>
        {pct(comment.aiScore)}
      </span>
      {categories.map(([name, confidence]) => (
        <span key={name} className={styles.chip}>{CHAT_CATEGORY_LABELS[name] ?? name} {Math.round(confidence * 100)}</span>
      ))}
    </div>
  );
};

function isDemo(): boolean {
  try { return new URLSearchParams(window.location.search).has('demoCommentaires'); }
  catch { return false; }
}

// ---- Démonstration (?demoCommentaires) ----

const DEMO_STARTED = Date.now();
const DEMO_DONE    = new Set<number>();

function demoComments(): ModerationComment[] {
  const base = (id: number, author: string, text: string, minutes: number, aiScore: number | null,
                aiCategories: Record<string, number>): ModerationComment => ({
    id, ownerId: id, author, videoId: 120, videoTitle: 'Clip de lancement', creator: null, text,
    creationDate: new Date(Date.now() - minutes * 60_000), status: 'pending', aiScore, aiCategories, aiEngine: 'google',
  });
  const all = [
    base(1, 'Lucas B.', 'Franchement nul, vous devriez arrêter.', 42, 0.52, { Insult: 0.44, Toxic: 0.38 }),
    base(2, 'Inès M.', 'Super vidéo ! Retrouvez la suite sur www.monsite-promo.com', 18, 0.08, {}),
  ];
  if (Date.now() - DEMO_STARTED > 15_000) {
    all.push(base(3, 'Karim T.', "T'es vraiment un idiot, personne ne te regarde.", 0, 0.66, { Insult: 0.71, Toxic: 0.58 }));
  }
  return all.filter(c => !DEMO_DONE.has(c.id));
}

export default CommentModerationList;
